import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useEffect, useState } from "react";
import {
  addLitigation,
  editComplaint,
  editSummon,
  fetchAssignedProsecutor,
} from "services/advocateComplaintService";
import {
  AdvocateComplaints,
  AssignedProsecutor,
  Investigation,
  Prosecutor,
  Summon,
} from "types";
import { LoadingIcon } from "utils/constant";
import { useTranslation } from "contexts/i18nContext";
import "../../complaintRequest/styles/AddEvidenceModal.css";

interface DecisionModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: Summon;
  complaintData: AdvocateComplaints;
  investigationData: Investigation;
  onRefresh: any;
  onComplaintsRefresh: any;
}

export default function SummonConfirmationModal({
  isOpen,
  setIsOpen,
  complaintData,
  summonData,
  investigationData,
  onRefresh,
  onComplaintsRefresh,
}: DecisionModalProps) {
  const { t } = useTranslation();
  // const canCoordinatorConfirmSummon = useHasRole([
  //   ROLES.INSPECTION_COORDINATOR,
  //   ROLES.INSPECTION_DIRECTOR,
  // ]);
  // const canAccusedConfirmSummon = useHasRole([ROLES.INSPECTION_PROSECUTOR]);

  const { showToast } = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  // const [error, setError] = useState<string | null>(null);
  const [prosecutorsData, setProsecutorsData] = useState<AssignedProsecutor[]>(
    [],
  );
  // const [showDropdown, setShowDropdown] = useState(false);

  // const [accusedProsecutorsData, setAccusedProsecutorsData] = useState<
  //   AccusedProsecutor[]
  // >([]);
  const [formState, setFormState] = useState({
    assigned_prosecutor_id: "",
    assigned_prosecutor: null as Prosecutor | null,
  });

  useEffect(() => {
    const loadProsecutors = async () => {
      try {
        const data = await fetchAssignedProsecutor(
          user?.admin_unit,
          user?.org_unit,
          user?.access,
        );

        setProsecutorsData(
          data?.filter((item) =>
            (item.roles as string[]).includes("AdvocateDisciplineProsecutor"),
          ),
        );
        // setError("");
      } catch (err) {
        // setError("Failed to load prosecutors.");
      } finally {
        setLoading(false);
      }
    };
    loadProsecutors();
  }, [user]);

  const filtered = prosecutorsData?.filter((p) =>
    `${p?.user?.first_name} ${p?.user?.last_name}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (prosecutor: Prosecutor, name: string) => {
    setFormState((prevState) => ({
      ...prevState,
      [name]: prosecutor,
    }));
    setQuery("");
    // setShowDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!summonData) return;

    setLoading(true);

    // const isAccusedConfirmed = summonData?.accused_confirmed
    //   ? summonData?.accused_confirmed
    //   : canAccusedConfirmSummon;
    const isAccusedConfirmed = summonData?.accused_confirmed
      ? summonData?.accused_confirmed
      : false;

    // const isCoordinatorConfirmed = summonData?.coordinator_confirmed
    //   ? summonData?.coordinator_confirmed
    //   : canCoordinatorConfirmSummon;

    const data = new FormData();
    data.append("accused_confirmed", isAccusedConfirmed.toString());
    data.append("coordinator_confirmed", true.toString());
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append("accused_advocate_name", complaintData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      complaintData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      complaintData?.accused_license_number || "",
    );
    data.append(
      "userid_assigned_prosecutor",
      formState.assigned_prosecutor_id || "",
    );
    data.append(
      "assigned_prosecutor",
      `${formState?.assigned_prosecutor?.first_name || ""} ${
        formState?.assigned_prosecutor?.last_name || ""
      }` || "",
    );

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      const resData = await editSummon(data, summon_id, isFormData);

      if (resData) {
        await addLitigation(
          {
            user_id_assigned_prosecutor: formState.assigned_prosecutor_id,
            assigned_prosecutor:
              `${formState?.assigned_prosecutor?.first_name || ""} ${
                formState?.assigned_prosecutor?.last_name || ""
              }` || "",

            admin_unit_id: complaintData?.admin_unit_id || "",
            org_id: complaintData?.org_id || "",
            accused_advocate_name: complaintData?.accused_name || "",
            user_id_accused_advocate: complaintData?.userid_accused_name || "",
            accused_license_number: complaintData?.accused_license_number || "",
            summon: summonData?.id ?? null,
          },
          false,
        );

        await editComplaint({
          ...complaintData,
          user_id_assigned_prosecutor: formState.assigned_prosecutor_id,
          assigned_prosecutor:
            `${formState?.assigned_prosecutor?.first_name || ""} ${
              formState?.assigned_prosecutor?.last_name || ""
            }` || "",
        });
      }

      onRefresh();
      onComplaintsRefresh();
      setIsOpen(false);
      showToast(t("confirmed"), "success");
      setFormState({
        assigned_prosecutor_id: "",
        assigned_prosecutor: null,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal isOpen={isOpen} setIsOpen={setIsOpen} title={t("confirmSummon")}>
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("assignProsecutor")}
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="assigned_prosecutor_id"
              value={formState.assigned_prosecutor_id}
              onChange={(e) => {
                handleChange(e);
                const selected = filtered?.find(
                  (p) => p.user.id === e.target.value,
                );

                if (selected && selected.user) {
                  handleSelect(selected.user, "assigned_prosecutor");
                }
              }}
            >
              <option value={""}>{t("selectProsecutor")}</option>
              {filtered?.map((p) => (
                <option key={p.user.id} value={p.user.id}>
                  {p.user?.first_name} ({p.user?.last_name})
                </option>
              ))}
            </select>
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("confirm")
          )}
        </button>
      </form>
    </SideModal>
  );
}
